import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useWorkspaceId } from '@/contexts/WorkspaceContext';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';
import { isLaunched } from '@/lib/campaignStatus';
import { canOpenFromUrl, isStepId, resumeStep, stepIndex, type StepId } from '@/lib/campaignSteps';
import { channelLabel, parseChannel } from '@/lib/campaignChannel';
import CampaignResultsPanel from '@/components/CampaignResultsPanel';
import CampaignSentMessage, { type SentCampaign, type SentMedia } from '@/components/CampaignSentMessage';
import CampaignStepper from '@/components/campaign-wizard/CampaignStepper';
import CampaignSetupWizard from '@/components/campaign-wizard/CampaignSetupWizard';
import DetailsStep from '@/components/campaign-wizard/DetailsStep';
import type { LaunchableCampaign } from '@/hooks/useCampaignLaunchState';

// One campaign, one stepper: basic details → WhatsApp template → media → contacts → final preview &
// launch → results. The channel is not a step: the Campaigns tab the campaign was created from
// decides it (`?channel=` on /campaigns/new), and it is fixed from then on.
//
// Before launch this page IS the editor, one step at a time, with a live preview of the message
// beside it. After launch the same page shows the last step - what was sent and how it did.
// Launching does not navigate anywhere: the stepper simply moves to Results.
//
// Two routes render it. `/campaigns/new` has no campaign yet - the row is created when step 1 is
// submitted - and sits behind SubscriptionGate because it leads to a create. `/campaigns/:id` is
// deliberately NOT gated: a workspace whose subscription lapsed must still be able to see what its
// last campaign did.

interface CampaignMediaSummary extends SentMedia {
  id: string;
  name: string;
}

// Extends the shape CampaignSentMessage reads, so the page and the preview cannot disagree about
// what the launched campaign carries — `metadata.sentMessage`, the template and its
// sendRequirements all arrive from GET /campaigns/:id and are typed in one place.
interface Campaign extends SentCampaign {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  status: string;
  createdAt: string;
  campaignMediaId: string | null;
  launchConfig?: LaunchableCampaign['launchConfig'];
  media?: CampaignMediaSummary | null;
}

const formatDateTime = (value?: string | null) => (value
  ? new Date(value).toLocaleString(undefined, {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
  : '—');

interface CampaignDetailProps {
  /** Rendered by `/campaigns/new`: nothing to fetch, only step 1. */
  isNew?: boolean;
}

export default function CampaignDetail({ isNew = false }: CampaignDetailProps) {
  const { t } = useTranslation();
  const workspaceId = useWorkspaceId();
  const navigate = useNavigate();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStep = searchParams.get('step');
  // Only read on /campaigns/new. Validated, so a hand-edited URL cannot create an unknown type.
  const newChannel = parseChannel(searchParams.get('channel'));

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const showError = useApiErrorToast();

  // "Create campaign" on an approved template lands here as `?template=<id>`: the new campaign
  // starts from that template and the media it was made from (the server fills both in). Loaded
  // before step 1 renders, so the name it suggests never lands on top of something being typed.
  const fromTemplateId = isNew ? searchParams.get('template') : null;
  const [fromTemplate, setFromTemplate] = useState<{ id: string; name: string } | null>(null);
  const [loadingTemplate, setLoadingTemplate] = useState(Boolean(fromTemplateId));

  useEffect(() => {
    if (!fromTemplateId || !workspaceId) return;
    let cancelled = false;

    (async () => {
      try {
        const template = await apiFetch<{ id: string; name: string; status: string }>(
          `/workspaces/${workspaceId}/whatsapp-templates/${fromTemplateId}`,
        );
        if (cancelled) return;
        if (template.status === 'APPROVED') {
          setFromTemplate({ id: template.id, name: template.name });
        } else {
          // The server would refuse it anyway; better to say why and carry on without it.
          toast.error('That template is still waiting for WhatsApp to approve it, so this campaign starts without it.');
        }
      } catch (error) {
        if (!cancelled) showError(error, 'Could not load that template.');
      } finally {
        if (!cancelled) setLoadingTemplate(false);
      }
    })();

    return () => { cancelled = true; };
  }, [fromTemplateId, workspaceId, showError]);

  const launched = isLaunched(campaign?.status);
  const listUrl = `/w/${workspaceId}/campaigns`;

  useEffect(() => {
    if (isNew || !workspaceId || !campaignId) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const data = await apiFetch<{ campaign: Campaign }>(
          `/workspaces/${workspaceId}/campaigns/${campaignId}`,
        );
        if (!cancelled) setCampaign(data.campaign);
      } catch (error) {
        if (!cancelled) showError(error, 'Failed to load this campaign.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isNew, workspaceId, campaignId, showError]);

  // The step the page actually shows. A step the URL names is honoured only when the saved campaign
  // allows it - an old link to `?step=launch` on a campaign with no media opens where it can be
  // continued instead.
  const step: StepId = isNew
    ? 'details'
    : campaign
      ? (isStepId(requestedStep) && canOpenFromUrl(requestedStep, campaign) ? requestedStep : resumeStep(campaign))
      : 'details';

  const setStep = useCallback((next: StepId, { replace = false }: { replace?: boolean } = {}) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('step', next);
      return params;
    }, { replace });
  }, [setSearchParams]);

  // Keep the URL saying where the operator is, without adding a history entry for the correction.
  useEffect(() => {
    if (isNew || !campaign || requestedStep === step) return;
    setStep(step, { replace: true });
  }, [isNew, campaign, requestedStep, step, setStep]);

  // A new step starts at its top. Without this, Next at the bottom of a long contact list lands
  // the operator halfway down the following step.
  const goToStep = useCallback((next: StepId) => {
    setStep(next);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [setStep]);

  if (loading || loadingTemplate) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  const title = isNew
    ? t('campaigns.new_title', 'New campaign')
    : campaign?.name || 'Campaign';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <Link
          to={listUrl}
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          Back to campaigns
        </Link>

        <div className="mb-6 min-w-0">
          <h1 className="text-3xl font-bold break-words">{title}</h1>
          {campaign?.description && (
            <p className="mt-1 max-w-3xl text-muted-foreground whitespace-pre-line line-clamp-3" dir="auto">
              {campaign.description}
            </p>
          )}
          {campaign && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={launched ? 'default' : 'outline'}>{campaign.status}</Badge>
              <span>{channelLabel(campaign.type)}</span>
              {campaign.media && (
                <>
                  <span>·</span>
                  <Link
                    to={`/w/${workspaceId}/campaigns/media/${campaign.media.id}`}
                    className="underline decoration-dotted hover:text-foreground"
                  >
                    {campaign.media.name}
                  </Link>
                </>
              )}
              <span>· Created {formatDateTime(campaign.createdAt)}</span>
            </div>
          )}
        </div>

        {isNew ? (
          <>
            <CampaignStepper
              current="details"
              isReachable={() => false}
              isComplete={() => false}
              onSelect={() => {}}
            />
            <DetailsStep
              workspaceId={workspaceId}
              campaign={null}
              channel={newChannel}
              fromTemplate={fromTemplate}
              // `replace`, so Back from the channel step returns to the list rather than to an empty
              // "new campaign" form for a campaign that now exists.
              onSaved={(created) => navigate(`${listUrl}/${created.id}?step=channel`, { replace: true })}
              onCancel={() => navigate(listUrl)}
            />
          </>
        ) : !campaign ? (
          <Card className="p-12 text-center text-muted-foreground">Campaign not found.</Card>
        ) : launched ? (
          <>
            {/* Every setup step is done and locked: the server refuses changes to a launched
                campaign, so offering to reopen them would be a trap. */}
            <CampaignStepper
              current="results"
              isReachable={() => false}
              isComplete={(id) => stepIndex(id) < stepIndex('results')}
              onSelect={() => {}}
            />
            <CampaignResultsPanel
              workspaceId={workspaceId}
              campaignId={campaign.id}
              campaignName={campaign.name}
              // Beside the KPI tiles, not above them: what was sent and how it landed are one thought,
              // and the panel owns the stats so it has to be the thing that places them.
              aside={(
                <Card className="h-full">
                  <CardContent className="pt-6">
                    <h2 className="mb-4 text-lg font-semibold">Message sent</h2>
                    {/* The message as it was received, not an inventory of the creative — the media
                        library is where the assets belong, and this only links to them. */}
                    <CampaignSentMessage campaign={campaign} workspaceId={workspaceId} />
                  </CardContent>
                </Card>
              )}
            />
          </>
        ) : (
          <CampaignSetupWizard
            campaign={campaign}
            workspaceId={workspaceId}
            step={step as Exclude<StepId, 'results'>}
            onStepChange={goToStep}
            onCampaignChange={(next) => setCampaign((prev) => (prev ? { ...prev, ...next } : prev))}
            // No navigation: the page swaps from editor to results in place, which is the whole
            // payoff of the steps living together. The URL follows via the correction effect.
            onLaunched={(next) => setCampaign((prev) => (prev ? { ...prev, ...next } : prev))}
            onExit={() => navigate(listUrl)}
          />
        )}
      </div>
    </div>
  );
}
